import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	Dimmer,
	Message,
	Dropdown,
	Form,
	Header,
	Icon,
	Button,
} from 'semantic-ui-react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { parse } from '../../utils/queryString';
import { SimpleFormWrapper } from '../SimpleFormWrapper/SimpleFormWrapper';
import { Spinner } from '../Spinner/Spinner';
import { validateEmail } from '../../utils/validateEmail';
import { AppState } from '../../types/app-state';
import { InviteUsersState } from '../../types/inviteusers-state';
import { UpdateSelectedOrganisationId } from '../../actions/inviteusers.actions';
import {
	AddUser,
	Destroy,
	Initialize,
	RemoveUser,
	SubmitInvite,
} from '../../actions/inviteusers.actions';
import './InviteUsersPage.less';
interface InviteUsersFormFields {
	email: string;
	firstName: string;
	lastName: string;
	role: string;
}

const roleOptions = [
	{
		key: 'role_org_admin',
		value: 'Organisation Admin',
		text: 'Organisation Admin',
	},
	{ key: 'role_coordinator', value: 'Coordinator', text: 'Coordinator' },
];

export const InviteUsersPage: React.FC = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const {
		loading,
		message,
		organisations,
		selectedOrganisationId: selectedOrgId,
		list,
		complete,
	} = useSelector<AppState, Partial<InviteUsersState>>(
		(state) => state.inviteusers
	);
	const orgOptions = (organisations ?? [])
		.filter((i) => !i.deactivatedAt)
		.map((org) => ({ key: 'org_' + org.id, value: org.id, text: org.name }));
	const currentOrg = selectedOrgId
		? (organisations ?? []).find((org) => org.id === selectedOrgId)
		: null;

	const handleOrgSelect = (_: any, data: any) => {
		console.log(data.value);
		dispatch(UpdateSelectedOrganisationId(data.value));
	};

	const handleAddMember = (
		values: InviteUsersFormFields,
		actions: FormikHelpers<InviteUsersFormFields>
	) => {
		dispatch(AddUser(values));
		actions.resetForm();
	};

	useEffect(() => {
		const queryParams = parse(location.search);
		dispatch(Initialize(queryParams.orgId as string));
		return () => {
			dispatch(Destroy());
		};
	}, [dispatch, location]);

	return (
		<div className='inviteusers-page'>
			<Dimmer inverted active={loading}>
				<Spinner />
			</Dimmer>
			<SimpleFormWrapper className='middle'>
				{!complete && (
					<>
						{message && (
							<Message negative>
								<p>{message}</p>
							</Message>
						)}
						<Header as='h3' className='header'>
							<span>Invite users to</span>
							<Dropdown
								search
								selection
								placeholder='Select Organisation'
								value={selectedOrgId}
								onChange={handleOrgSelect}
								options={orgOptions}
							/>
						</Header>
						<Formik
							onSubmit={handleAddMember}
							initialValues={{
								email: '',
								role: '',
								firstName: '',
								lastName: '',
							}}
							validate={(values: InviteUsersFormFields) => {
								const errors: Partial<InviteUsersFormFields> = {};

								if (!values.email) {
									errors.email = 'Email is required';
								} else if (!validateEmail(values.email)) {
									errors.email = 'Email is invalid';
								} else if (
									(list ?? []).find((user) => user.email === values.email)
								) {
									errors.email = 'Email is already in the list';
								}

								if (!values.role) {
									errors.role = 'Role is required';
								} else if (
									!(
										values.role === 'Organisation Admin' ||
										values.role === 'Coordinator'
									)
								) {
									errors.role = 'Role is invalid';
								}

								return errors;
							}}
						>
							{({
								values,
								errors,
								touched,
								handleChange,
								handleBlur,
								handleSubmit,
								setFieldValue,
								isValid,
							}: FormikProps<InviteUsersFormFields>) => (
								<Form className='inviteusers-form' onSubmit={handleSubmit}>
									<Form.Group>
										<Form.Input
											autoFocus
											type='email'
											name='email'
											placeholder='Email'
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.email}
											error={!!(touched.email && errors.email)}
											required
										/>
										<Form.Input
											type='text'
											name='firstName'
											placeholder='First Name'
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.firstName}
											error={!!(touched.firstName && errors.firstName)}
											required
										/>
										<Form.Input
											type='text'
											name='lastName'
											placeholder='Last Name'
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.lastName}
											error={!!(touched.lastName && errors.lastName)}
											required
										/>
										<Form.Field>
											<Dropdown
												fluid
												search
												selection
												id='role'
												name='role'
												options={roleOptions}
												placeholder='Role'
												onChange={(event: any, data: any) => {
													setFieldValue('role', data.value);
												}}
												value={values.role}
												error={!!(touched.role && errors.role)}
												required
											/>
										</Form.Field>
										<Form.Button disabled={!isValid} icon type='submit'>
											<Icon name='add' />
										</Form.Button>
									</Form.Group>
								</Form>
							)}
						</Formik>
						<ul className='user-list'>
							{(list ?? []).map((user) => (
								<li key={'iu_' + user.email}>
									<span>
										<b>{user.email}</b> as {user.role}
									</span>
									<Button
										icon
										circular
										onClick={() => dispatch(RemoveUser(user.email))}
									>
										<Icon name='remove' />
									</Button>
								</li>
							))}
						</ul>
						<Button
							disabled={(list ?? []).length === 0}
							primary
							onClick={() => dispatch(SubmitInvite())}
						>
							Send Invites
						</Button>
					</>
				)}
				{complete && currentOrg && (
					<>
						<h3>Invitations have been sent for {currentOrg.name}</h3>
						<p>
							Users will receive a link to continue their registration for this
							organisation.
						</p>
						<Button primary as={Link} to={'/'}>
							Home
						</Button>
					</>
				)}
			</SimpleFormWrapper>
		</div>
	);
};

export default InviteUsersPage;
